import { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { unAuthenticatedApiService } from 'services/service';
import { ApiEndpoints } from 'services/endpoints';

/* ─────────────────────────────────────────────────────────────
   useVendorProducts.js
   Fetches products for a single vendor by name.

   Backend: GET /requests/zoho/get-products?vendor_name=...&page=...&per_page=...
   No Redux. Local state only. Mirrors useVendors pattern:
     - AbortController to cancel stale requests
     - Debounced search input
     - Stable extras key to avoid re-fetch loops
     - Returns { products, isLoading, error, hasMore, totalCount, refetch }
   ───────────────────────────────────────────────────────────── */

const stripNil = (obj) => {
    const result = {};
    Object.keys(obj || {}).forEach((key) => {
        const value = obj[key];
        if (value !== null && value !== undefined && value !== '') {
            result[key] = value;
        }
    });
    return result;
};

export const useVendorProducts = ({
    vendorName = null,
    page = 1,
    per_page = 12,
    search = null,
    extras = {},
    debounce = 400,
} = {}) => {
    const [products, setProducts]     = useState([]);
    const [isLoading, setIsLoading]   = useState(false);
    const [error, setError]           = useState(null);
    const [hasMore, setHasMore]       = useState(false);
    const [totalCount, setTotalCount] = useState(0);

    /* Debounce the search input */
    const [debouncedSearch, setDebouncedSearch] = useState(search);
    useEffect(() => {
        if (debounce <= 0) {
            setDebouncedSearch(search);
            return;
        }
        const handle = setTimeout(() => setDebouncedSearch(search), debounce);
        return () => clearTimeout(handle);
    }, [search, debounce]);

    /* Stable reference for `extras` */
    const extrasKey = useMemo(() => JSON.stringify(extras || {}), [extras]);

    /* AbortController so stale responses can't overwrite newer ones */
    const abortRef = useRef(null);

    const fetchProducts = useCallback(async () => {
        /* No vendor name? Don't fetch. Leave products as empty. */
        if (!vendorName) {
            setProducts([]);
            setTotalCount(0);
            setHasMore(false);
            return;
        }

        /* Cancel any in-flight request from a previous render */
        if (abortRef.current) {
            abortRef.current.abort();
        }
        const controller = new AbortController();
        abortRef.current = controller;

        setIsLoading(true);
        setError(null);

        try {
            const params = stripNil({
                vendor_name: vendorName,
                page,
                per_page,
                name: debouncedSearch,
                ...JSON.parse(extrasKey),
            });

            const response = await unAuthenticatedApiService.get(
                ApiEndpoints.GET_PRODUCTS,
                {
                    params,
                    signal: controller.signal,
                },
            );

            if (controller.signal.aborted) return;

            /* Backend wraps the actual items in response.data — match the existing
               action's expectation. Items array might be `items`, `data`, or `products`
               depending on endpoint. Try the common shapes. */
            const payload = response?.data ?? {};
            const items =
                payload?.items
                ?? payload?.data
                ?? payload?.products
                ?? (Array.isArray(payload) ? payload : []);

            const pageCtx = payload?.page_context ?? {};

            setProducts(items);
            setHasMore(Boolean(pageCtx?.has_more_page));
            setTotalCount(pageCtx?.total ?? items.length);
        } catch (err) {
            if (err?.name === 'CanceledError' || err?.name === 'AbortError') return;
            setError(err);
            setProducts([]);
        } finally {
            if (!controller.signal.aborted) {
                setIsLoading(false);
            }
        }
    }, [vendorName, page, per_page, debouncedSearch, extrasKey]);

    useEffect(() => {
        fetchProducts();
        return () => {
            if (abortRef.current) abortRef.current.abort();
        };
    }, [fetchProducts]);

    return {
        products,
        isLoading,
        error,
        hasMore,
        totalCount,
        refetch: fetchProducts,
    };
};

export default useVendorProducts;